import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { User, Crown, RotateCcw, LogOut, Mail, Target, Coins, Sparkles, Share2, Trophy } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { CLASSES, getClass, levelFromXp } from "@/lib/rpg";
import { api, formatApiErrorDetail } from "@/lib/api";
import { card, label, btnSecondary, btnPrimary, tagHi, tag } from "@/lib/styles";

const FLEX_COST = 200;

const LEVEL_LABELS = { beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced", top_1: "Top 1%" };
const CLUSTER_LABELS = {
  basics: "AI basics", prompting: "Prompting", rag: "RAG & retrieval", evaluation: "Evaluation",
  agents: "Agents", finetuning: "Fine-tuning", production: "Production & cost", systemdesign: "System design",
};

export default function Account() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [minting, setMinting] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  if (!user) return null;
  const lvl = levelFromXp(user.xp || 0);
  const klass = getClass(user.character_class);
  const gold = user.gold || 0;

  const chooseClass = async (id) => {
    try {
      const { data } = await api.post("/user/class", { character_class: id });
      setUser(data);
      toast.success(`Class set: ${getClass(id)?.name}`);
    } catch { toast.error("Could not change class"); }
  };

  const manageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  const mintFlex = async () => {
    if (minting) return;
    setMinting(true);
    try {
      const { data } = await api.post("/flex-card/mint");
      setUser(data.user);
      toast.success(data.already ? "You already have a flex card" : "Flex card minted!");
      navigate(`/card/${data.user.user_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setMinting(false); }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className={label}>account</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Settings</h1>

        <div className={`${card} p-6 mt-8`}>
          <div className="flex items-center gap-4">
            {klass
              ? <img src={klass.sigil} alt="" className="w-16 h-16 select-none" style={{ WebkitMaskImage: "radial-gradient(circle,#000 60%,transparent 72%)", maskImage: "radial-gradient(circle,#000 60%,transparent 72%)" }} />
              : <div className="w-14 h-14 rounded-full border border-gold/40 bg-gold/5 flex items-center justify-center"><User className="text-gold" /></div>}
            <div>
              <p className="font-serif font-bold text-lg text-zinc-100">{user.name}</p>
              <p className="text-sm text-zinc-500 flex items-center gap-1"><Mail size={13} /> {user.email}</p>
              <p className="text-xs text-gold mt-1">{lvl.rank} · Level {lvl.level} · <span className="inline-flex items-center gap-1"><Coins size={12} /> {user.gold || 0}</span></p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {(user.gauntlet_wins || 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gold border border-gold/40 bg-gold/5 px-3 py-1.5 rounded-md" data-testid="account-gauntlet-badge">
                <Trophy size={14} /> Gauntlet champion ×{user.gauntlet_wins}
              </span>
            )}
            {user.plan === "pro"
              ? <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-yellow-400 border border-yellow-500/50 bg-yellow-950/30 px-3 py-1.5 rounded-md"><Crown size={14} /> Pro member</span>
              : <span className={tag} data-testid="account-plan">Free plan · {user.trial_interviews_used}/3 trial interviews used</span>}
            {user.plan === "pro"
              ? <button onClick={manageSubscription} disabled={portalLoading} className="text-xs text-gold hover:underline uppercase tracking-wider" data-testid="account-manage-billing">{portalLoading ? "redirecting..." : "Manage subscription →"}</button>
              : <button onClick={() => navigate("/pricing")} className="text-xs text-gold hover:underline uppercase tracking-wider" data-testid="account-upgrade">Upgrade →</button>}
          </div>
        </div>

        {/* CLASS */}
        <div className={`${card} p-6 mt-6`}>
          <span className={label}>your class</span>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CLASSES.map((c) => (
              <button
                key={c.id}
                data-testid={`account-class-${c.id}`}
                onClick={() => chooseClass(c.id)}
                className={`text-left border rounded-lg p-3 transition-all flex gap-3 items-center ${user.character_class === c.id ? "border-gold/70 bg-gold/5" : "border-zinc-800 hover:border-zinc-700"}`}
              >
                <img src={c.sigil} alt="" className="w-12 h-12 shrink-0 select-none" style={{ WebkitMaskImage: "radial-gradient(circle,#000 60%,transparent 72%)", maskImage: "radial-gradient(circle,#000 60%,transparent 72%)" }} />
                <div>
                  <p className="font-serif font-bold text-sm text-zinc-100">{c.name}</p>
                  <p className={`text-[10px] uppercase tracking-wider ${c.tint}`}>{c.title}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={`${card} p-6 mt-6`}>
          <span className={label}>placement</span>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-zinc-400">Level:</span>
            <span className={tagHi}>{LEVEL_LABELS[user.placement_level] || "not set"}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-zinc-400 flex items-center gap-1"><Target size={13} /> Focus:</span>
            {(user.focus_areas || []).length === 0 ? <span className="text-xs text-zinc-600">none selected</span> : user.focus_areas.map((f) => <span key={f} className={tag}>{CLUSTER_LABELS[f] || f}</span>)}
          </div>
          <button onClick={() => navigate("/onboarding")} className={`${btnSecondary} mt-5`} data-testid="retake-placement"><RotateCcw size={14} /> Retake placement</button>
        </div>

        {/* FLEX CARD */}
        <div className={`${card} p-6 mt-6`}>
          <span className={label}><Sparkles size={12} className="inline -mt-0.5" /> flex card</span>
          <p className="text-sm text-zinc-400 mt-2">Mint a public, shareable card of your stats, download it as an image or drop the link on X / LinkedIn.</p>
          {user.flex_minted ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/card/${user.user_id}`} className={btnPrimary} data-testid="account-flex-view"><Share2 size={16} /> View / share my card</Link>
            </div>
          ) : (
            <div className="mt-4">
              <button onClick={mintFlex} disabled={minting || gold < FLEX_COST} className={btnPrimary} data-testid="account-flex-mint">
                <Sparkles size={16} /> Mint flex card · −{FLEX_COST} gold
              </button>
              {gold < FLEX_COST && <p className="text-[11px] text-rose-400/80 mt-2">Need {FLEX_COST} gold, you have {gold}.</p>}
            </div>
          )}
        </div>

        <button onClick={async () => { await logout(); navigate("/"); }} className={`${btnPrimary} mt-6`} data-testid="account-logout"><LogOut size={16} /> Log out</button>
      </div>
    </div>
  );
}
